import { Component, HostBinding, OnDestroy } from '@angular/core';
import { DsButtonVariants, DsTooltipPosition, Icons, IconStyles, DsBreakpoints } from '@levelaccess/design-system';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { WindowService } from '../../../services/window.service';
import { LauncherNavService } from '../../../services/navigation/launcher-nav.service';
import { BrowserLocalStorageService, LOCAL_STORAGE } from '../../../services/browser-local-storage.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { VerticalNavigationStateService } from '../vertical-navigation-state.service';
import { VerticalNavCollapseOverrideService } from '../../../services/vertical-nav-collapse-override.service';

@Component({
  selector: 'vertical-navigation-panel',
  templateUrl: './vertical-navigation-panel.component.html',
  styleUrls: ['./vertical-navigation-panel.component.scss'],
  standalone: false,
})
export class VerticalNavigationPanelComponent implements OnDestroy {
  @HostBinding('class.collapsed')
  public isCollapsed: boolean = false;

  public hasAccessToLauncher: boolean;
  public DsTooltipPosition: typeof DsTooltipPosition = DsTooltipPosition;
  public DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public IconStyles: typeof IconStyles = IconStyles;
  public Icons: typeof Icons = Icons;

  private subscriptions: Subscription = new Subscription();
  private localStorageSubs: Subscription = new Subscription();
  private isMobile: boolean = false;
  private enhancementsEnabled: boolean = false;
  private isForceCollapsed: boolean = false;

  constructor(
    private windowService: WindowService,
    private launcherNavService: LauncherNavService,
    private browserLocalStorageService: BrowserLocalStorageService,
    private featureFlagService: FeatureFlagService,
    private verticalNavigationStateService: VerticalNavigationStateService,
    private verticalNavCollapseOverrideService: VerticalNavCollapseOverrideService,
  ) {
    this.hasAccessToLauncher = this.launcherNavService.hasAccessToLauncher;

    this.subscriptions.add(
      this.verticalNavigationStateService.collapseRequests$.subscribe(() => {
        this.isCollapsed = true;
      }),
    );

    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.verticalNavEnhancements, false)
        .pipe(map(Boolean), distinctUntilChanged())
        .subscribe((enabled: boolean) => {
          this.enhancementsEnabled = enabled;
          this.localStorageSubs.unsubscribe();
          this.localStorageSubs = new Subscription();

          if (enabled) {
            this.isCollapsed = this.browserLocalStorageService.getItem(LOCAL_STORAGE.SIDEBAR_COLLAPSED) ?? false;

            this.localStorageSubs.add(
              this.browserLocalStorageService
                .observeKey$<boolean>(LOCAL_STORAGE.SIDEBAR_COLLAPSED)
                .subscribe((value: boolean | null) => {
                  if (!this.isMobile && !this.isForceCollapsed) {
                    this.isCollapsed = value ?? false;
                  }
                }),
            );
          }
        }),
    );

    this.subscriptions.add(
      this.windowService.observeBreakpoint(DsBreakpoints.Large).subscribe((isMobile: boolean) => {
        this.isMobile = isMobile;
        if (isMobile) {
          this.isCollapsed = true;
        } else if (this.enhancementsEnabled && !this.isForceCollapsed) {
          this.isCollapsed = this.browserLocalStorageService.getItem(LOCAL_STORAGE.SIDEBAR_COLLAPSED) ?? false;
        }
      }),
    );

    this.subscriptions.add(
      this.verticalNavCollapseOverrideService.forceCollapsed$.subscribe((forceCollapsed: boolean) => {
        // Deferred to a new macrotask: this can fire synchronously from another component's
        // effect() mid change-detection, and mutating isCollapsed inline would change an
        // already-checked host binding, triggering NG0100 (ExpressionChangedAfterItHasBeenCheckedError).
        setTimeout(() => {
          if (forceCollapsed === this.isForceCollapsed) {
            return;
          }
          this.isForceCollapsed = forceCollapsed;
          // Once auto-collapsed, the nav stays collapsed - it never auto-restores, even after
          // the override is released (e.g. the chat panel closes), per AI-2360.
          if (forceCollapsed) {
            this.isCollapsed = true;
          }
        });
      }),
    );
  }

  public toggleMenu(): void {
    this.isCollapsed = !this.isCollapsed;
    if (!this.isCollapsed) {
      this.verticalNavCollapseOverrideService.notifyNavExpanded();
    }
    if (this.enhancementsEnabled) {
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.SIDEBAR_COLLAPSED, this.isCollapsed);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.localStorageSubs.unsubscribe();
  }
}
